import React, { useState, useEffect } from 'react';
import {
  X, ChevronRight, ChevronLeft, Globe, Settings, Shield, Zap, Brain,
  CheckCircle, Lock, Key, Search, Database, FileJson, AlertTriangle,
  Server, Code, Eye, Layers, RefreshCw, Target, Info, Copy, ExternalLink
} from 'lucide-react';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const APIScanWizard = ({ isOpen, onClose, onComplete }) => {
  // Get active workspace from context
  const { activeWorkspace } = useWorkspace();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Scan Name
    name: '', // Optional scan name

    // Workspace - will be auto-set from context
    workspace_id: '',

    // Step 1: Target Configuration
    target_url: '',
    swagger_url: '',
    openapi_spec: '',
    auth_type: 'none',
    api_key: '',
    api_key_header: 'X-API-Key',
    bearer_token: '',
    basic_username: '',
    basic_password: '',
    oauth_client_id: '',
    oauth_client_secret: '',
    oauth_token_url: '',
    custom_headers: '',

    // Step 2: Reconnaissance Tests
    recon_endpoint_discovery: true,
    recon_swagger_parse: true,
    recon_swaggerspy_osint: true, // SwaggerSpy OSINT on SwaggerHub
    recon_wordlist_fuzzing: true,
    recon_method_enumeration: true,
    recon_version_detection: true,
    recon_tech_fingerprint: true,
    recon_rate_limit_check: true,
    recon_cors_check: true,

    // Step 3: Authentication Tests
    auth_bypass_tests: true,
    auth_jwt_analysis: true,
    auth_token_manipulation: false,
    auth_session_fixation: true,
    auth_brute_force: false,
    auth_password_policy: true,
    auth_mfa_bypass: false,
    auth_oauth_misconfig: true,

    // Step 4: Access Control Tests
    access_bola_idor: true,
    access_bfla: true,
    access_horizontal_priv: true,
    access_vertical_priv: true,
    access_mass_assignment: true,
    access_rate_limiting: true,
    access_resource_exhaustion: true,

    // Step 5: Injection Tests
    injection_sql: true,
    injection_nosql: true,
    injection_command: true,
    injection_xxe: true,
    injection_ssrf: true,
    injection_ssti: true,
    injection_ldap: false,
    injection_xpath: false,
    injection_graphql: true,

    // Step 6: Business Logic Tests
    business_flow_bypass: true,
    business_price_manipulation: false,
    business_quantity_tampering: false,
    business_coupon_abuse: false,
    business_workflow_skip: true,
    business_time_based: true,
    business_race_condition: true,

    // Step 7: Report Configuration
    ai_analysis: true,
    ai_model: 'sylink',
    ai_assistance_mode: 'auto', // 'auto' or 'manual'
    report_format: 'html',
    include_remediation: true,
    include_poc: true,
    include_curl_examples: true,
    severity_filter: ['critical', 'high', 'medium'],
    compliance_frameworks: [],
  });

  // Auto-set workspace_id from active workspace
  useEffect(() => {
    if (activeWorkspace?.id && !formData.workspace_id) {
      setFormData(prev => ({ ...prev, workspace_id: activeWorkspace.id }));
    }
  }, [activeWorkspace?.id]);

  const steps = [
    { id: 1, title: 'Target API', icon: Globe, desc: 'URL & Authentication' },
    { id: 2, title: 'Reconnaissance', icon: Search, desc: 'Discovery & Enumeration' },
    { id: 3, title: 'Authentication', icon: Key, desc: 'Auth Testing' },
    { id: 4, title: 'Access Control', icon: Lock, desc: 'BOLA/BFLA Tests' },
    { id: 5, title: 'Injection', icon: Code, desc: 'Injection Attacks' },
    { id: 6, title: 'Business Logic', icon: Layers, desc: 'Logic Flaws' },
    { id: 7, title: 'Report', icon: CheckCircle, desc: 'Configuration' },
  ];

  const handleNext = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onComplete(formData);
      onClose();
    } catch (error) {
      // Check for insufficient credits error (402)
      if (error.response?.status === 402) {
        const detail = error.response?.data?.detail;
        if (detail?.error === 'insufficient_credits') {
          alert(`Crédits insuffisants pour lancer ce scan.\n\nCrédits requis: ${detail.message || 'N/A'}\n\nVeuillez acheter des crédits ou passer à un plan supérieur.`);
        } else {
          alert('Crédits insuffisants. Veuillez acheter des crédits pour continuer.');
        }
      } else {
        alert('Erreur lors de la création du scan: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  // Test Category Component
  const TestCategory = ({ title, icon: Icon, tests, color = 'brand-green' }) => (
    <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-2 bg-${color}/20 rounded-lg`}>
          <Icon className={`w-5 h-5 text-${color}`} />
        </div>
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
      <div className="space-y-3">
        {tests.map((test) => (
          <label key={test.field} className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={formData[test.field]}
              onChange={() => handleCheckboxChange(test.field)}
              className="mt-1 w-4 h-4 accent-brand-green rounded"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium group-hover:text-brand-green transition-colors">
                  {test.name}
                </span>
                {test.risk && (
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    test.risk === 'high' ? 'bg-red-500/20 text-red-400' :
                    test.risk === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {test.risk}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">{test.desc}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );

  if (!isOpen) return null;

  // Render Step Content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            {/* Scan Name */}
            <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Target className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Nom du scan</h3>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nom (optionnel)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Ex: Audit API Production, Test E-commerce..."
                  className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Donnez un nom personnalisé pour identifier facilement ce scan
                </p>
              </div>
            </div>

            {/* Target URL */}
            <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-brand-green/20 rounded-lg">
                  <Globe className="w-5 h-5 text-brand-green" />
                </div>
                <h3 className="text-lg font-semibold text-white">Target API</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Base URL&nbsp;<span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.target_url}
                    onChange={(e) => handleInputChange('target_url', e.target.value)}
                    placeholder="https://api.exemple.com/v1"
                    className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Swagger/OpenAPI URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={formData.swagger_url}
                    onChange={(e) => handleInputChange('swagger_url', e.target.value)}
                    placeholder="https://api.exemple.com/swagger.json"
                    className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Provide OpenAPI spec for better endpoint discovery
                  </p>
                </div>
              </div>
            </div>

            {/* Authentication */}
            <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Key className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Authentication</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Auth Type
                  </label>
                  <select
                    value={formData.auth_type}
                    onChange={(e) => handleInputChange('auth_type', e.target.value)}
                    className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-brand-green focus:outline-none"
                  >
                    <option value="none">No Authentication</option>
                    <option value="api_key">API Key</option>
                    <option value="bearer">Bearer Token (JWT)</option>
                    <option value="basic">Basic Auth</option>
                    <option value="oauth2">OAuth 2.0</option>
                  </select>
                </div>

                {formData.auth_type === 'api_key' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        API Key Header Name
                      </label>
                      <input
                        type="text"
                        value={formData.api_key_header}
                        onChange={(e) => handleInputChange('api_key_header', e.target.value)}
                        placeholder="X-API-Key"
                        className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        API Key Value
                      </label>
                      <input
                        type="password"
                        value={formData.api_key}
                        onChange={(e) => handleInputChange('api_key', e.target.value)}
                        placeholder="votre-cle-api"
                        className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {formData.auth_type === 'bearer' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Bearer Token (JWT)
                    </label>
                    <textarea
                      value={formData.bearer_token}
                      onChange={(e) => handleInputChange('bearer_token', e.target.value)}
                      placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                      rows={3}
                      className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-brand-green focus:outline-none font-mono text-sm"
                    />
                  </div>
                )}

                {formData.auth_type === 'basic' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Username
                      </label>
                      <input
                        type="text"
                        value={formData.basic_username}
                        onChange={(e) => handleInputChange('basic_username', e.target.value)}
                        className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-brand-green focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Password
                      </label>
                      <input
                        type="password"
                        value={formData.basic_password}
                        onChange={(e) => handleInputChange('basic_password', e.target.value)}
                        className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-brand-green focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {formData.auth_type === 'oauth2' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Client ID
                        </label>
                        <input
                          type="text"
                          value={formData.oauth_client_id}
                          onChange={(e) => handleInputChange('oauth_client_id', e.target.value)}
                          className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-brand-green focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Client Secret
                        </label>
                        <input
                          type="password"
                          value={formData.oauth_client_secret}
                          onChange={(e) => handleInputChange('oauth_client_secret', e.target.value)}
                          className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-brand-green focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Token URL
                      </label>
                      <input
                        type="url"
                        value={formData.oauth_token_url}
                        onChange={(e) => handleInputChange('oauth_token_url', e.target.value)}
                        placeholder="https://auth.exemple.com/oauth/token"
                        className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-brand-green focus:outline-none"
                      />
                    </div>
                  </div>
                )}

                {/* Custom Headers */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Custom Headers (Optional)
                  </label>
                  <textarea
                    value={formData.custom_headers}
                    onChange={(e) => handleInputChange('custom_headers', e.target.value)}
                    placeholder="X-Custom-Header: value&#10;X-Another-Header: value"
                    rows={3}
                    className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:border-brand-green focus:outline-none font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">Un en-tête par ligne au format : Nom-En-Tete: valeur</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <TestCategory
              title="Endpoint Discovery"
              icon={Search}
              tests={[
                { field: 'recon_endpoint_discovery', name: 'Endpoint Fuzzing', desc: 'Discover hidden endpoints using wordlists (ffuf, gobuster)' },
                { field: 'recon_swagger_parse', name: 'OpenAPI/Swagger Parsing', desc: 'Extract endpoints from API documentation' },
                { field: 'recon_swaggerspy_osint', name: 'SwaggerSpy OSINT', desc: 'Search SwaggerHub for exposed API docs, leaked credentials & secrets', risk: 'high' },
                { field: 'recon_wordlist_fuzzing', name: 'Parameter Fuzzing', desc: 'Discover hidden parameters and query strings' },
                { field: 'recon_method_enumeration', name: 'HTTP Method Enumeration', desc: 'Test all HTTP methods on each endpoint' },
              ]}
            />

            <TestCategory
              title="Fingerprinting"
              icon={Eye}
              tests={[
                { field: 'recon_version_detection', name: 'Version Detection', desc: 'Identify API version and framework' },
                { field: 'recon_tech_fingerprint', name: 'Technology Stack', desc: 'Detect backend technology and libraries' },
                { field: 'recon_rate_limit_check', name: 'Rate Limit Analysis', desc: 'Identify rate limiting mechanisms' },
                { field: 'recon_cors_check', name: 'CORS Configuration', desc: 'Analyze Cross-Origin Resource Sharing settings' },
              ]}
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <TestCategory
              title="Authentication Bypass"
              icon={Key}
              tests={[
                { field: 'auth_bypass_tests', name: 'Auth Bypass Attempts', desc: 'Test common authentication bypass techniques' },
                { field: 'auth_jwt_analysis', name: 'JWT Analysis', desc: 'Analyze JWT tokens for weaknesses (algorithm confusion, weak secrets)' },
                { field: 'auth_token_manipulation', name: 'Token Manipulation', desc: 'Attempt to forge or modify authentication tokens', risk: 'medium' },
                { field: 'auth_session_fixation', name: 'Session Fixation', desc: 'Test for session fixation vulnerabilities' },
              ]}
            />

            <TestCategory
              title="Credential Testing"
              icon={Lock}
              tests={[
                { field: 'auth_brute_force', name: 'Brute Force (Limited)', desc: 'Test common credentials with rate limiting', risk: 'high' },
                { field: 'auth_password_policy', name: 'Password Policy Check', desc: 'Analyze password requirements and enforcement' },
                { field: 'auth_mfa_bypass', name: 'MFA Bypass Tests', desc: 'Test multi-factor authentication bypass techniques', risk: 'medium' },
                { field: 'auth_oauth_misconfig', name: 'OAuth Misconfiguration', desc: 'Test for OAuth flow vulnerabilities' },
              ]}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <TestCategory
              title="Object Level Authorization"
              icon={Database}
              tests={[
                { field: 'access_bola_idor', name: 'BOLA/IDOR Testing', desc: 'Broken Object Level Authorization - access other users\' data' },
                { field: 'access_bfla', name: 'BFLA Testing', desc: 'Broken Function Level Authorization - access admin functions' },
                { field: 'access_horizontal_priv', name: 'Horizontal Privilege Escalation', desc: 'Access resources of same-level users' },
                { field: 'access_vertical_priv', name: 'Vertical Privilege Escalation', desc: 'Elevate from user to admin privileges' },
              ]}
            />

            <TestCategory
              title="Resource Protection"
              icon={Shield}
              tests={[
                { field: 'access_mass_assignment', name: 'Mass Assignment', desc: 'Test for unprotected object property binding' },
                { field: 'access_rate_limiting', name: 'Rate Limit Bypass', desc: 'Attempt to bypass rate limiting controls' },
                { field: 'access_resource_exhaustion', name: 'Resource Exhaustion', desc: 'Test API resource limits and quotas' },
              ]}
            />
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <TestCategory
              title="SQL & NoSQL Injection"
              icon={Database}
              tests={[
                { field: 'injection_sql', name: 'SQL Injection', desc: 'Test for SQL injection in all parameters (sqlmap)' },
                { field: 'injection_nosql', name: 'NoSQL Injection', desc: 'Test for MongoDB/NoSQL injection patterns' },
                { field: 'injection_graphql', name: 'GraphQL Injection', desc: 'Test GraphQL-specific injection techniques' },
              ]}
            />

            <TestCategory
              title="Command & Template Injection"
              icon={Code}
              tests={[
                { field: 'injection_command', name: 'Command Injection', desc: 'Test for OS command injection' },
                { field: 'injection_ssti', name: 'SSTI (Template)', desc: 'Server-Side Template Injection tests' },
                { field: 'injection_xxe', name: 'XXE Injection', desc: 'XML External Entity injection' },
                { field: 'injection_ssrf', name: 'SSRF', desc: 'Server-Side Request Forgery tests' },
              ]}
            />

            <TestCategory
              title="Advanced Injection"
              icon={AlertTriangle}
              tests={[
                { field: 'injection_ldap', name: 'LDAP Injection', desc: 'Test for LDAP query injection', risk: 'low' },
                { field: 'injection_xpath', name: 'XPath Injection', desc: 'Test for XPath query injection', risk: 'low' },
              ]}
            />
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <TestCategory
              title="Business Flow Tests"
              icon={Layers}
              tests={[
                { field: 'business_flow_bypass', name: 'Flow Bypass', desc: 'Skip required steps in business workflows' },
                { field: 'business_workflow_skip', name: 'Step Skipping', desc: 'Bypass mandatory workflow steps' },
                { field: 'business_time_based', name: 'Time-Based Attacks', desc: 'Exploit time-sensitive operations' },
                { field: 'business_race_condition', name: 'Race Conditions', desc: 'Test for race condition vulnerabilities' },
              ]}
            />

            <TestCategory
              title="E-Commerce Logic"
              icon={Target}
              tests={[
                { field: 'business_price_manipulation', name: 'Price Manipulation', desc: 'Attempt to modify prices in requests', risk: 'medium' },
                { field: 'business_quantity_tampering', name: 'Quantity Tampering', desc: 'Modify quantities for discount exploitation', risk: 'medium' },
                { field: 'business_coupon_abuse', name: 'Coupon/Discount Abuse', desc: 'Test coupon reuse and stacking', risk: 'low' },
              ]}
            />
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            {/* AI Analysis */}
            <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <Brain className="w-5 h-5 text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">AI Analysis</h3>
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ai_analysis}
                    onChange={() => handleCheckboxChange('ai_analysis')}
                    className="w-5 h-5 accent-brand-green rounded"
                  />
                  <div>
                    <span className="text-white font-medium">Enable AI-Powered Analysis</span>
                    <p className="text-sm text-gray-500">Use DeepSeek R1 to analyze findings and generate recommendations</p>
                  </div>
                </label>

                {formData.ai_analysis && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Modèle IA
                      </label>
                      <select
                        value={formData.ai_model}
                        onChange={(e) => handleInputChange('ai_model', e.target.value)}
                        className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-brand-green focus:outline-none"
                      >
                        <option value="sylink">SYLink AI (Recommandé)</option>
                        <option value="sylink-thinking">SYLink Thinking 32B</option>
                        <option value="deepseek-r1">DeepSeek R1</option>
                        <option value="qwen3">Qwen3 (Rapide)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-3">
                        Mode d'assistance IA
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => handleInputChange('ai_assistance_mode', 'auto')}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            formData.ai_assistance_mode === 'auto'
                              ? 'border-brand-green bg-brand-green/10'
                              : 'border-gray-700 hover:border-brand-green/50'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 rounded-lg ${formData.ai_assistance_mode === 'auto' ? 'bg-brand-green/20' : 'bg-gray-800'}`}>
                              <Brain className="w-5 h-5 text-brand-green" />
                            </div>
                            <div className="font-semibold text-white">Auto</div>
                          </div>
                          <div className="text-xs text-gray-500 text-left">
                            L'IA analyse automatiquement et prend des decisions en temps reel.
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleInputChange('ai_assistance_mode', 'manual')}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            formData.ai_assistance_mode === 'manual'
                              ? 'border-blue-500 bg-blue-500/10'
                              : 'border-gray-700 hover:border-blue-500/50'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div className={`p-2 rounded-lg ${formData.ai_assistance_mode === 'manual' ? 'bg-blue-500/20' : 'bg-gray-800'}`}>
                              <Settings className="w-5 h-5 text-blue-400" />
                            </div>
                            <div className="font-semibold text-white">Manuel</div>
                          </div>
                          <div className="text-xs text-gray-500 text-left">
                            L'IA suggere mais vous gardez le controle total.
                          </div>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Report Configuration */}
            <div className="bg-brand-black rounded-xl p-5 border border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-brand-green/20 rounded-lg">
                  <FileJson className="w-5 h-5 text-brand-green" />
                </div>
                <h3 className="text-lg font-semibold text-white">Report Configuration</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Report Format
                  </label>
                  <select
                    value={formData.report_format}
                    onChange={(e) => handleInputChange('report_format', e.target.value)}
                    className="w-full bg-brand-black-light border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-brand-green focus:outline-none"
                  >
                    <option value="html">HTML (Interactive)</option>
                    <option value="pdf">PDF (Printable)</option>
                    <option value="json">JSON (Machine-readable)</option>
                    <option value="markdown">Markdown</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-brand-black-light rounded-lg border border-gray-700 hover:border-brand-green/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.include_remediation}
                      onChange={() => handleCheckboxChange('include_remediation')}
                      className="w-4 h-4 accent-brand-green"
                    />
                    <span className="text-gray-300">Include Remediation</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-brand-black-light rounded-lg border border-gray-700 hover:border-brand-green/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.include_poc}
                      onChange={() => handleCheckboxChange('include_poc')}
                      className="w-4 h-4 accent-brand-green"
                    />
                    <span className="text-gray-300">Include PoC Code</span>
                  </label>

                  <label className="flex items-center gap-3 cursor-pointer p-3 bg-brand-black-light rounded-lg border border-gray-700 hover:border-brand-green/50 transition-colors">
                    <input
                      type="checkbox"
                      checked={formData.include_curl_examples}
                      onChange={() => handleCheckboxChange('include_curl_examples')}
                      className="w-4 h-4 accent-brand-green"
                    />
                    <span className="text-gray-300">Include cURL Examples</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Severity Filter
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['critical', 'high', 'medium', 'low', 'info'].map((severity) => (
                      <label
                        key={severity}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${
                          formData.severity_filter.includes(severity)
                            ? severity === 'critical' ? 'bg-purple-500/30 border-purple-500 text-purple-400' :
                              severity === 'high' ? 'bg-red-500/30 border-red-500 text-red-400' :
                              severity === 'medium' ? 'bg-orange-500/30 border-orange-500 text-orange-400' :
                              severity === 'low' ? 'bg-yellow-500/30 border-yellow-500 text-yellow-400' :
                              'bg-blue-500/30 border-blue-500 text-blue-400'
                            : 'bg-gray-800 border-gray-700 text-gray-400'
                        } border`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.severity_filter.includes(severity)}
                          onChange={() => {
                            const newFilter = formData.severity_filter.includes(severity)
                              ? formData.severity_filter.filter(s => s !== severity)
                              : [...formData.severity_filter, severity];
                            handleInputChange('severity_filter', newFilter);
                          }}
                          className="sr-only"
                        />
                        <span className="capitalize">{severity}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gradient-to-br from-brand-green/10 to-blue-500/10 rounded-xl p-5 border border-brand-green/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-brand-green/20 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-brand-green" />
                </div>
                <h3 className="text-lg font-semibold text-white">Scan Summary</h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-brand-black/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-brand-green">
                    {Object.keys(formData).filter(k => k.startsWith('recon_') && formData[k]).length}
                  </div>
                  <div className="text-xs text-gray-400">Recon Tests</div>
                </div>
                <div className="bg-brand-black/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-400">
                    {Object.keys(formData).filter(k => k.startsWith('auth_') && formData[k]).length}
                  </div>
                  <div className="text-xs text-gray-400">Auth Tests</div>
                </div>
                <div className="bg-brand-black/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-orange-400">
                    {Object.keys(formData).filter(k => k.startsWith('injection_') && formData[k]).length}
                  </div>
                  <div className="text-xs text-gray-400">Injection Tests</div>
                </div>
                <div className="bg-brand-black/50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-purple-400">
                    {Object.keys(formData).filter(k => (k.startsWith('access_') || k.startsWith('business_')) && formData[k]).length}
                  </div>
                  <div className="text-xs text-gray-400">Logic Tests</div>
                </div>
              </div>

              <div className="mt-4 p-3 bg-brand-black/50 rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Target className="w-4 h-4 text-brand-green" />
                  <span className="text-gray-300">Target:</span>
                  <span className="text-white font-mono">{formData.target_url || 'Not specified'}</span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-brand-black-light border border-brand-green/30 rounded-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden shadow-2xl my-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-800">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">API Security Scan</h2>
              <p className="text-gray-400">Comprehensive REST/GraphQL API pentesting</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-brand-black border-b border-gray-800 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => setCurrentStep(step.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      isActive
                        ? 'bg-brand-green text-brand-black'
                        : isCompleted
                        ? 'bg-brand-green/20 text-brand-green'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium whitespace-nowrap">{step.title}</span>
                  </button>
                  {index < steps.length - 1 && (
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isCompleted ? 'text-brand-green' : 'text-gray-600'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-250px)]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-800 bg-brand-black">
          <button
            onClick={handleBack}
            disabled={currentStep === 1}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
              currentStep === 1
                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-lg font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>

            {currentStep < steps.length ? (
              <button
                onClick={handleNext}
                disabled={currentStep === 1 && !formData.target_url}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-bold transition-all ${
                  currentStep === 1 && !formData.target_url
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700'
                }`}
              >
                Next
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !formData.target_url}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg font-bold transition-all ${
                  isSubmitting || !formData.target_url
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700 shadow-lg'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Launching...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Launch API Scan
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIScanWizard;
